<!-- ─── Header Row ─────────────────────────────────────────────────── -->
<div class="ad-header">
  <h2 class="ad-title">Analytics Dashboard</h2>
  <div class="ad-range-buttons">
    <button [class.active]="selectedDays === 7"  (click)="selectDays(7)">7 Days</button>
    <button [class.active]="selectedDays === 14" (click)="selectDays(14)">14 Days</button>
    <button [class.active]="selectedDays === 30" (click)="selectDays(30)">30 Days</button>
  </div>
</div>

<!-- ─── Loading Spinner ────────────────────────────────────────────── -->
@if (loading) {
  <div class="ad-spinner-wrap">
    <app-spinner [formLoading]="loading"></app-spinner>
  </div>
}

<!-- ─── Error State ────────────────────────────────────────────────── -->
@if (!loading && errorMessage) {
  <div class="ad-error">
    <span class="ad-error-icon">&#9888;</span>
    {{ errorMessage }}
  </div>
}

<!-- ─── Content (only when data is loaded) ────────────────────────── -->
@if (!loading && !errorMessage && data) {

  <!-- ─── Alert Strip ──────────────────────────────────────────────── -->
  @if (data.alerts && data.alerts.length > 0) {
    <div class="ad-alerts">
      @for (alert of data.alerts; track alert) {
        <div class="ad-alert-item">
          <span class="ad-alert-icon">&#9888;</span>
          {{ alert }}
        </div>
      }
    </div>
  }

  <!-- ─── Stat Cards ───────────────────────────────────────────────── -->
  <div class="ad-stat-grid">

    <!-- Total Users -->
    <div class="ad-stat-card">
      <p class="ad-stat-label">TOTAL USERS</p>
      <p class="ad-stat-value">{{ formatNumber(data.summary.current.totalUsers) }}</p>
      <div class="ad-stat-change"
           [class.positive]="isPositiveChange(data.summary.current.totalUsers, data.summary.prior.totalUsers)"
           [class.negative]="!isPositiveChange(data.summary.current.totalUsers, data.summary.prior.totalUsers)">
        <span class="ad-arrow">
          {{ isPositiveChange(data.summary.current.totalUsers, data.summary.prior.totalUsers) ? '▲' : '▼' }}
        </span>
        {{ pctChange(data.summary.current.totalUsers, data.summary.prior.totalUsers) }}
        <span class="ad-prior">vs {{ formatNumber(data.summary.prior.totalUsers) }}</span>
      </div>
    </div>

    <!-- Sessions -->
    <div class="ad-stat-card">
      <p class="ad-stat-label">SESSIONS</p>
      <p class="ad-stat-value">{{ formatNumber(data.summary.current.sessions) }}</p>
      <div class="ad-stat-change"
           [class.positive]="isPositiveChange(data.summary.current.sessions, data.summary.prior.sessions)"
           [class.negative]="!isPositiveChange(data.summary.current.sessions, data.summary.prior.sessions)">
        <span class="ad-arrow">
          {{ isPositiveChange(data.summary.current.sessions, data.summary.prior.sessions) ? '▲' : '▼' }}
        </span>
        {{ pctChange(data.summary.current.sessions, data.summary.prior.sessions) }}
        <span class="ad-prior">vs {{ formatNumber(data.summary.prior.sessions) }}</span>
      </div>
    </div>

    <!-- Page Views -->
    <div class="ad-stat-card">
      <p class="ad-stat-label">PAGE VIEWS</p>
      <p class="ad-stat-value">{{ formatNumber(data.summary.current.screenPageViews) }}</p>
      <div class="ad-stat-change"
           [class.positive]="isPositiveChange(data.summary.current.screenPageViews, data.summary.prior.screenPageViews)"
           [class.negative]="!isPositiveChange(data.summary.current.screenPageViews, data.summary.prior.screenPageViews)">
        <span class="ad-arrow">
          {{ isPositiveChange(data.summary.current.screenPageViews, data.summary.prior.screenPageViews) ? '▲' : '▼' }}
        </span>
        {{ pctChange(data.summary.current.screenPageViews, data.summary.prior.screenPageViews) }}
        <span class="ad-prior">vs {{ formatNumber(data.summary.prior.screenPageViews) }}</span>
      </div>
    </div>

    <!-- Avg Session Duration -->
    <div class="ad-stat-card">
      <p class="ad-stat-label">AVG SESSION</p>
      <p class="ad-stat-value">{{ formatDuration(data.summary.current.averageSessionDuration) }}</p>
      <div class="ad-stat-change"
           [class.positive]="isPositiveChange(data.summary.current.averageSessionDuration, data.summary.prior.averageSessionDuration)"
           [class.negative]="!isPositiveChange(data.summary.current.averageSessionDuration, data.summary.prior.averageSessionDuration)">
        <span class="ad-arrow">
          {{ isPositiveChange(data.summary.current.averageSessionDuration, data.summary.prior.averageSessionDuration) ? '▲' : '▼' }}
        </span>
        {{ pctChange(data.summary.current.averageSessionDuration, data.summary.prior.averageSessionDuration) }}
        <span class="ad-prior">vs {{ formatDuration(data.summary.prior.averageSessionDuration) }}</span>
      </div>
    </div>

  </div>

  <!-- ─── Booking Funnel Chart ─────────────────────────────────────── -->
  <div class="ad-chart-card">
    <h3 class="ad-section-title">Booking Funnel — Users by Step (last {{ selectedDays }} days)</h3>
    <div class="ad-funnel-wrap">
      <p-chart type="bar" [data]="funnelChartData" [options]="funnelChartOptions" height="220px"></p-chart>
    </div>
  </div>

  <!-- ─── Traffic by Channel Table ─────────────────────────────────── -->
  <div class="ad-chart-card">
    <h3 class="ad-section-title">Traffic by Channel</h3>
    <table class="ad-table">
      <thead>
        <tr>
          <th>Channel</th>
          <th class="align-right">Users</th>
          <th class="align-right">Sessions</th>
          <th class="align-right">Bounce Rate</th>
          <th class="align-right">Avg Session</th>
        </tr>
      </thead>
      <tbody>
        @for (ch of sortedChannels; track ch.sessionDefaultChannelGroup) {
          <tr [class.ad-row-warn]="+ch.bounceRate > 0.80">
            <td>{{ ch.sessionDefaultChannelGroup }}</td>
            <td class="align-right">{{ formatNumber(ch.totalUsers) }}</td>
            <td class="align-right">{{ formatNumber(ch.sessions) }}</td>
            <td class="align-right">{{ formatPercent(ch.bounceRate) }}</td>
            <td class="align-right">{{ formatDuration(ch.averageSessionDuration) }}</td>
          </tr>
        }
        @if (sortedChannels.length === 0) {
          <tr><td colspan="5" class="ad-empty">No channel data available.</td></tr>
        }
      </tbody>
    </table>
  </div>

  <!-- ─── Top Pages Table ───────────────────────────────────────────── -->
  <div class="ad-chart-card">
    <h3 class="ad-section-title">Top Pages</h3>
    <table class="ad-table">
      <thead>
        <tr>
          <th>Page Path</th>
          <th class="align-right">Views</th>
          <th class="align-right">Users</th>
          <th class="align-right">Bounce Rate</th>
        </tr>
      </thead>
      <tbody>
        @for (page of topPages; track page.pagePath) {
          <tr [class.ad-row-danger]="+page.bounceRate > 0.90">
            <td>
              <span [title]="page.pagePath">{{ truncatePath(page.pagePath) }}</span>
            </td>
            <td class="align-right">{{ formatNumber(page.screenPageViews) }}</td>
            <td class="align-right">{{ formatNumber(page.totalUsers) }}</td>
            <td class="align-right">{{ formatPercent(page.bounceRate) }}</td>
          </tr>
        }
        @if (topPages.length === 0) {
          <tr><td colspan="4" class="ad-empty">No page data available.</td></tr>
        }
      </tbody>
    </table>
  </div>

}
